/**
 * The table and the toolbar of a list, drawn from `useListTable`'s state
 * (interface I-1): the kit's `DataTable`, `Pagination`, `PageSize` and
 * `NoMatches`, and a toolbar in `TableToolbar`'s manner with the filter
 * chips in it and an export at its right-hand end.
 */

import {
  Button,
  DataTable,
  type DataTableProps,
  Input,
  NoMatches,
  PageSize,
  Pagination,
  Select,
} from '@fiveox/ui'
import { type MouseEvent, type ReactNode, useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router'

import { useListLoading } from './ListPage'
import type { ListColumn, ListTableState } from './useListTable'

/** Below Tailwind's `sm` — where a list shows only its first columns. */
const PHONE_QUERY = '(max-width: 639px)'

export function useIsPhone(): boolean {
  const read = () =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(PHONE_QUERY).matches
      : false
  const [phone, setPhone] = useState(read)
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return
    const media = window.matchMedia(PHONE_QUERY)
    const onChange = () => setPhone(media.matches)
    media.addEventListener?.('change', onChange)
    return () => media.removeEventListener?.('change', onChange)
  }, [])
  return phone
}

/** The columns a phone shows: those marked `phone`, or else the first two
 *  and the one keyed `status`. */
export function phoneColumns<T>(columns: ListColumn<T>[]): ListColumn<T>[] {
  if (columns.some((c) => c.phone)) return columns.filter((c) => c.phone)
  return columns.filter((c, index) => index < 2 || c.key === 'status')
}

export interface ListToolbarProps<T> {
  list: ListTableState<T>
  /** What the search box says — *Search by file name*. */
  placeholder?: string
  /** More filters after the chips — a date range a report takes, say. */
  children?: ReactNode
  /** The right-hand end: `ExportButton`. */
  actions?: ReactNode
}

export function ListToolbar<T>({ list, placeholder = 'Search…', children, actions }: ListToolbarProps<T>) {
  const { toolbar } = list
  return (
    <div data-list-toolbar="" className="flex flex-wrap items-center gap-3">
      {toolbar.setSearch && (
        <div className="w-full sm:w-64">
          <Input
            size="sm"
            value={toolbar.search ?? ''}
            onChange={(e) => toolbar.setSearch!(e.target.value)}
            placeholder={placeholder}
            aria-label="Search"
          />
        </div>
      )}
      {list.filters.map(({ def, value, set }) => (
        <Select
          key={def.key}
          size="sm"
          // Side by side on a phone, each as wide as it needs from `sm` up —
          // up to 14rem, or a filter of client names is as wide as the
          // firm's longest and pushes the toolbar onto a second line.
          className="w-[calc(50vw-2rem)] sm:w-auto sm:max-w-[14rem]"
          aria-label={def.label}
          value={value}
          onChange={(e) => set(e.target.value)}
        >
          <option value="">{def.allLabel ?? `All ${def.label.toLowerCase()}`}</option>
          {def.options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Select>
      ))}
      {children}
      <span className="text-muted-foreground ml-auto text-xs" data-list-count="">
        {toolbar.shown === toolbar.total
          ? `${toolbar.total} ${toolbar.total === 1 ? 'row' : 'rows'}`
          : `${toolbar.shown} of ${toolbar.total}`}
      </span>
      {(toolbar.setSearch || list.filters.length > 0) && (
        <Button variant="ghost" size="sm" onClick={toolbar.onReset} disabled={!toolbar.canReset}>
          Reset
        </Button>
      )}
      {actions}
    </div>
  )
}

export interface ListTableProps<T> {
  list: ListTableState<T>
  rowKey: (row: T) => string
  /** Where a row opens. The first cell becomes a link to it (for the
   *  keyboard), and a click anywhere on the row goes there too. */
  rowHref?: (row: T) => string
  /** For a row that opens a dialog rather than a page. */
  onRowClick?: (row: T) => void
  /** Row actions, stuck to the right edge (the kit's `trailing`). */
  trailing?: DataTableProps<T>['trailing']
  /** Overrides `ListPage`'s loading, for a table outside one. */
  loading?: boolean
  /** What an empty table says when it is not inside a `ListPage` (a record's
   *  tab); inside one, the page's `EmptyState` has already said it. */
  empty?: ReactNode
  maxHeight?: string
}

export function ListTable<T extends object>({
  list,
  rowKey,
  rowHref,
  onRowClick,
  trailing,
  loading,
  empty,
  maxHeight,
}: ListTableProps<T>) {
  const pageLoading = useListLoading()
  const busy = loading ?? pageLoading
  const phone = useIsPhone()
  const navigate = useNavigate()

  if (!busy && list.noMatches) {
    return (
      <NoMatches
        query={list.narrowedBy}
        action={
          <Button variant="outline" size="sm" onClick={list.reset}>
            Clear the search and filters
          </Button>
        }
      >
        Nothing you hold matches. Clear the search and filters to see all {list.all.length}.
      </NoMatches>
    )
  }

  // On a phone the kept columns give up their minimum widths, so the three
  // of them fit a 390px screen without the table scrolling sideways.
  const shown = phone
    ? phoneColumns(list.table.columns).map((column) => ({ ...column, width: 0 }))
    : list.table.columns
  const columns: ListColumn<T>[] = rowHref
    ? shown.map((column, index) =>
        index === 0
          ? {
              ...column,
              render: (row: T) => (
                <Link
                  to={rowHref(row)}
                  onClick={(event: MouseEvent) => event.stopPropagation()}
                  className="text-foreground hover:text-primary focus-visible:ring-primary/40 rounded font-medium focus-visible:ring-2 focus-visible:outline-none"
                >
                  {column.render(row)}
                </Link>
              ),
            }
          : column,
      )
    : shown

  const open = rowHref ? (row: T) => navigate(rowHref(row)) : onRowClick

  return (
    <div className="min-w-0">
      <DataTable
        columns={columns}
        rows={list.table.rows}
        rowKey={rowKey}
        sortField={list.table.sortField}
        sortDir={list.table.sortDir}
        onSort={list.table.onSort}
        loading={busy}
        empty={empty}
        onRowClick={open}
        // Kept on a phone: a row action a phone could not reach is a verb lost.
        trailing={trailing}
        maxHeight={maxHeight}
        className="bg-card"
      />
      {!busy && (list.pagination.totalPages > 1 || list.sorted.length > 25) && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Pagination {...list.pagination} alwaysShow />
          <div className="mt-4">
            <PageSize pageSize={list.pageSize} setPageSize={list.setPageSize} />
          </div>
        </div>
      )}
    </div>
  )
}
